import React from 'react';
import { X, Plus, Save, Loader2 } from 'lucide-react';

interface AddFileModalProps {
  showAddFileModal: boolean;
  creatingFile: boolean;
  newFileData: any;
  selectedClient: any;
  language: 'en' | 'ar';
  strings: any;
  presetData: any;
  getFileTypeName: (typeID: string, language: 'en' | 'ar') => string;
  getFileStatusName: (statusID: string, language: 'en' | 'ar') => string;
  getCourtTypeName: (courtTypeID: string, language: 'en' | 'ar') => string;
  getCaseTypeName: (caseTypeID: string, language: 'en' | 'ar') => string;
  onClose: () => void;
  onInputChange: (field: string, value: any) => void;
  onSave: () => void;
}

const AddFileModal: React.FC<AddFileModalProps> = ({
  showAddFileModal,
  creatingFile,
  newFileData,
  selectedClient,
  language,
  strings,
  presetData,
  getFileTypeName,
  getFileStatusName,
  getCourtTypeName,
  getCaseTypeName,
  onClose,
  onInputChange,
  onSave
}) => {
  if (!showAddFileModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center text-white font-semibold text-lg">
                <Plus className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
                  {strings.legalFiles.addNewFileTitle}
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  {strings.legalFiles.addNewFileSubtitle.replace('{clientName}', selectedClient?.name || '')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">{strings.legalFiles.basicInformation}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                    {strings.legalFiles.fileTitleLabel} *
                  </label>
                  <input
                    type="text"
                    value={newFileData.title}
                    onChange={(e) => onInputChange('title', e.target.value)}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                    placeholder={strings.legalFiles.fileTitlePlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                    {strings.legalFiles.fileTypeLabel}
                  </label>
                  <select
                    value={newFileData.typeID}
                    onChange={(e) => onInputChange('typeID', e.target.value)}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                  >
                    <option value="">{strings.legalFiles.selectFileType}</option>
                    {presetData?.fileTypes.map((fileType: any) => (
                      <option key={fileType.id} value={fileType.id}>
                        {getFileTypeName(fileType.id, language as 'en' | 'ar')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                    {strings.legalFiles.statusLabel}
                  </label>
                  <select
                    value={newFileData.statusID}
                    onChange={(e) => onInputChange('statusID', e.target.value)}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                  >
                    <option value="">{strings.legalFiles.selectStatus}</option>
                    {presetData?.fileStatuses.map((fileStatus: any) => (
                      <option key={fileStatus.id} value={fileStatus.id}>
                        {getFileStatusName(fileStatus.id, language as 'en' | 'ar')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                  {strings.legalFiles.mainSubjectLabel} *
                </label>
                <textarea
                  value={newFileData.description}
                  onChange={(e) => onInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                  placeholder={strings.legalFiles.mainSubjectPlaceholder}
                />
              </div>
            </div>

            {/* Case Information - Only show if file type is "1" (case file) */}
            {newFileData.typeID === '1' && (
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">{strings.legalFiles.caseInformation}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      {strings.legalFiles.caseNumberLabel}
                    </label>
                    <input
                      type="text"
                      value={newFileData.caseNumber}
                      onChange={(e) => onInputChange('caseNumber', e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                      placeholder={strings.legalFiles.caseNumberPlaceholder}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      {strings.legalFiles.caseYearLabel}
                    </label>
                    <input
                      type="number"
                      value={newFileData.caseYear}
                      onChange={(e) => onInputChange('caseYear', e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                      placeholder={new Date().getFullYear().toString()}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      {strings.legalFiles.courtTypeLabel}
                    </label>
                    <select
                      value={newFileData.courtTypeID}
                      onChange={(e) => onInputChange('courtTypeID', e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                    >
                      <option value="">{strings.legalFiles.selectCourtType}</option>
                      {presetData?.courtTypes.map((courtType: any) => (
                        <option key={courtType.id} value={courtType.id}>
                          {getCourtTypeName(courtType.id, language as 'en' | 'ar')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      {strings.legalFiles.caseAreaLabel}
                    </label>
                    <input
                      type="text"
                      value={newFileData.caseArea}
                      onChange={(e) => onInputChange('caseArea', e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                      placeholder={strings.legalFiles.caseAreaPlaceholder}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      {strings.legalFiles.firstSessionDateLabel}
                    </label>
                    <input
                      type="date"
                      value={newFileData.caseFirstSessionDate}
                      onChange={(e) => onInputChange('caseFirstSessionDate', e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      {strings.legalFiles.opponentsLabel}
                    </label>
                    <input
                      type="text"
                      value={newFileData.caseOpponents}
                      onChange={(e) => onInputChange('caseOpponents', e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                      placeholder={strings.legalFiles.opponentsPlaceholder}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      {strings.legalFiles.caseTypeLabel}
                    </label>
                    <select
                      value={newFileData.caseTypeID}
                      onChange={(e) => onInputChange('caseTypeID', e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                    >
                      <option value="">{strings.legalFiles.selectCaseType}</option>
                      {presetData?.caseTypes.map((caseType: any) => (
                        <option key={caseType.id} value={caseType.id}>
                          {getCaseTypeName(caseType.id, language as 'en' | 'ar')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Financial Information */}
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">{strings.legalFiles.financialInformation}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                    {strings.legalFiles.expectedFeesLabel}
                  </label>
                  <input
                    type="number"
                    value={newFileData.expectedFees}
                    onChange={(e) => onInputChange('expectedFees', Number(e.target.value))}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                    {strings.legalFiles.totalIncomeLabel}
                  </label>
                  <input
                    type="number"
                    value={newFileData.totalIncome}
                    onChange={(e) => onInputChange('totalIncome', Number(e.target.value))}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                    {strings.legalFiles.totalExpensesLabel}
                  </label>
                  <input
                    type="number"
                    value={newFileData.totalExpenses}
                    onChange={(e) => onInputChange('totalExpenses', Number(e.target.value))}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">{strings.legalFiles.additionalNotes}</h3>
              <textarea
                value={newFileData.notes}
                onChange={(e) => onInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-slate-900 dark:text-white"
                placeholder={strings.legalFiles.additionalNotesPlaceholder}
              />
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors duration-200"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              onClick={onSave}
              disabled={creatingFile || !newFileData.title.trim() || !newFileData.description.trim()}
              className="flex items-center space-x-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creatingFile ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{language === 'ar' ? 'إنشاء الملف' : 'Create File'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFileModal;
